import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IGetFinancialsRequestParams } from "../../domain/IGetFinancialsRequestParams";
import { IProjectFinancials } from "../../domain/IProjectFinancials";
import { projectService } from "../../services/project.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IConsumableExemptionFinancials } from "../../domain/IConsumableExemptionFinancials";
import { IConcreteBaseFinancials } from "../../domain/IConcreteBaseFinancials";
import { IAccountReceivableFinancials } from "../../domain/IAccountReceivableFinancials";
import { IAccountPayableFinancials } from "../../domain/IAccountPayableFinancials";
import { IStockOutputFinancials } from "../../domain/IStockOutputFinancials";
import { ICarnetsFinancials } from "../../domain/ICarnetsFinancials";

export const getProjectFinancials = createAsyncThunk(
  "Projects/getProjectFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getProjectFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getAccountPayableFinancials = createAsyncThunk(
  "Projects/getAccountPayableFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getAccountPayableFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getAccountReceivableFinancials = createAsyncThunk(
  "Projects/getAccountReceivableFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getAccountReceivableFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getCarnetFinancials = createAsyncThunk(
  "Projects/getCarnetFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getCarnetFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getStockOutputFinancials = createAsyncThunk(
  "Projects/getStockOutputFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getStockOutputFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getProjectConsumableFinancials = createAsyncThunk(
  "Projects/getProjectConsumableFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getProjectConsumableFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

export const getProjectConcreteBaseFinancials = createAsyncThunk(
  "Projects/getProjectConcreteBaseFinancials",
  async (params: { data: IGetFinancialsRequestParams, signal: AbortSignal }) => {
    try {
      return await projectService.getProjectConcreteBaseFinancials(params.data, params.signal);
    } catch (err) {
      setError(err);
    }
  }
);

interface financialsState {
  generalFinancials: IProjectFinancials | undefined;
  accountPayableFinancials: IAccountPayableFinancials | undefined;
  accountReceivableFinancials: IAccountReceivableFinancials | undefined;
  carnetFinancials: ICarnetsFinancials | undefined;
  stockOutputFinancials: IStockOutputFinancials | undefined;
  consumableExemptionFinancials: IConsumableExemptionFinancials | undefined;
  concreteBaseOwnProductsFinancials: IConcreteBaseFinancials | undefined;
  selectedDateFrom: string | undefined;
  selectedDateTo: string | undefined;
  mealPriceSettingsPopover: IPopoverState;
  loading: boolean;
  pendingRequests: number;
  error: any;
}

const initialState: financialsState = {
  generalFinancials: undefined,
  accountPayableFinancials: undefined,
  accountReceivableFinancials: undefined,
  carnetFinancials: undefined,
  stockOutputFinancials: undefined,
  consumableExemptionFinancials: undefined,
  concreteBaseOwnProductsFinancials: undefined,
  selectedDateFrom: undefined,
  selectedDateTo: undefined,
  mealPriceSettingsPopover: { isOpen: false },
  loading: false,
  pendingRequests: 0,
  error: null,
};

export const financialsSlice = createSlice({
  name: "projects-financials",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setSelectedDates: (
      state,
      action: PayloadAction<{
        dateFrom: string | undefined; dateTo: string | undefined
      }>
    ) => {
      const { dateFrom, dateTo } = action.payload;
      state.selectedDateFrom = dateFrom;
      state.selectedDateTo = dateTo;
    },
    updateAccountPayableNote: (
      state,
      action: PayloadAction<{ index: number; note: string }>
    ) => {
      if (state.accountPayableFinancials) {
        state.accountPayableFinancials.accountPayableEntries[
          action.payload.index].note = action.payload.note;
      }
    },
    updateAccountReceivableNote: (
      state,
      action: PayloadAction<{ index: number; note: string }>
    ) => {
      if (state.accountReceivableFinancials) {
        state.accountReceivableFinancials.accountReceivableEntries[
          action.payload.index].note = action.payload.note;
      }
    },
    setMealPriceSettingsPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.mealPriceSettingsPopover = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProjectFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getProjectFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.generalFinancials = action.payload;
    });
    builder.addCase(getProjectFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getAccountPayableFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getAccountPayableFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.accountPayableFinancials = action.payload;
    });
    builder.addCase(getAccountPayableFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getAccountReceivableFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getAccountReceivableFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.accountReceivableFinancials = action.payload;
    });
    builder.addCase(getAccountReceivableFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getCarnetFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getCarnetFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.carnetFinancials = action.payload;
    });
    builder.addCase(getCarnetFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getStockOutputFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getStockOutputFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.stockOutputFinancials = action.payload;
    });
    builder.addCase(getStockOutputFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getProjectConsumableFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getProjectConsumableFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      if (action.payload) {
        state.consumableExemptionFinancials = action.payload;
      }
    });
    builder.addCase(getProjectConsumableFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
    builder.addCase(getProjectConcreteBaseFinancials.pending, (state) => {
      state.pendingRequests += 1;
      state.loading = state.pendingRequests > 0;
    });
    builder.addCase(getProjectConcreteBaseFinancials.fulfilled, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      if (action.payload) {
        state.concreteBaseOwnProductsFinancials = action.payload;
      }
    });
    builder.addCase(getProjectConcreteBaseFinancials.rejected, (state, action) => {
      state.pendingRequests -= 1;
      state.loading = state.pendingRequests > 0;
      state.error = action.error;
    });
  },
});

export const {
  setError,
  setSelectedDates,
  updateAccountPayableNote,
  updateAccountReceivableNote,
  setMealPriceSettingsPopover,
} = financialsSlice.actions;

export const selectState = (state: RootState) => state.projects.financials;
export default financialsSlice.reducer;
